"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildAllowed,
  type DrawResult,
  drawSantas,
  type HallViolation,
  MAX_PEOPLE,
  parseExclusions,
  parseNames,
} from "@/lib/tool-kit/random-fun/secret-santa";

/** English strings. A translated content file overrides these through `ui`. */
export const SECRET_SANTA_GENERATOR_STRINGS = {
  namesLabel: "Names — one per line",
  namesPlaceholder: "Ana\nBen\nCleo\nDev\nEli",
  namesHint:
    "Everyone who gives and receives a gift. Up to {max} names; each name must be different.",
  exclusionsLabel: "People who should not draw each other (optional)",
  exclusionsPlaceholder: "Ana, Ben\nCleo > Dev",
  exclusionsHint:
    "One rule per line. “Ana, Ben” means neither draws the other — use it for partners or siblings. “Cleo > Dev” means only that Cleo does not draw Dev — use it for last year’s match.",
  noSwapsLabel: "No swaps: nobody draws the person who drew them",
  noteLabel: "Note to add to each message (optional)",
  notePlaceholder: "Budget 20 dollars. We open gifts on 20 December.",
  example: "Fill in an example",
  clear: "Clear",
  draw: "Draw names",
  privacy:
    "Everything happens in this page. No names are sent, saved or emailed — you pass each person their match yourself.",
  results: "Your draw",
  resultsHint:
    "Matches are hidden so you do not see them by accident. Show one at a time, or copy a private message for each person.",
  showAll: "Show every match",
  placeholder: "Add at least two names, then press Draw names.",
  gives: "{giver} gives to…",
  show: "Show",
  hide: "Hide",
  showLabel: "Show the match for {giver}",
  hideLabel: "Hide the match for {giver}",
  hidden: "Hidden",
  copyMessage: "Copy message for {giver}",
  message: "{giver}, you are the Secret Santa for {receiver}.",
  messageWithNote: "{giver}, you are the Secret Santa for {receiver}. {note}",
  copyAll: "Copy all pairs",
  copied: "Copied!",
  pairLine: "{giver} → {receiver}",
  announce: "Drew {count} matches. They are hidden until you show them.",
  guided:
    "These rules were tight, so the draw used a guided search instead of a plain shuffle. The matches are valid, but not every valid draw was equally likely.",
  redraw: "Draw again",
  problemTooFew: "Add at least two different names.",
  problemTooMany: "That is too many names. The limit is {max}.",
  problemDuplicate:
    "“{name}” appears more than once. Give each person a different name.",
  problemUnknown:
    "The rule “{line}” names “{name}”, who is not in the list of names.",
  problemUnreadable:
    "The rule “{line}” can’t be read. Write two names separated by a comma, or two names separated by >.",
  impossibleNobody:
    "{name} cannot draw anyone, because every other person is excluded. Remove a rule to continue.",
  impossibleGroup:
    "These {count} {peopleWord} can only give to {receivers}, which is fewer people than there are givers: {givers}. Remove one of their rules to continue.",
  peopleWord: "one: person|other: people",
  noSwapsImpossible:
    "With “no swaps” and these rules there is no valid draw. Untick “no swaps” or remove a rule.",
  gaveUp:
    "No valid draw was found within the search limit. Try removing a rule or unticking “no swaps”.",
} as const;

type Strings = typeof SECRET_SANTA_GENERATOR_STRINGS;

const EXAMPLE_NAMES = "Ana\nBen\nCleo\nDev\nEli\nFay";
const EXAMPLE_RULES = "Ana, Ben\nCleo > Dev";

function listNames(indexes: readonly number[], names: readonly string[]) {
  return indexes.map((i) => names[i] ?? "").join(", ");
}

function impossibleMessage(
  violation: HallViolation,
  names: readonly string[],
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): string {
  const first = violation.givers[0];
  if (violation.givers.length === 1 && violation.receivers.length === 0) {
    return t(strings.impossibleNobody, { name: names[first ?? 0] ?? "" });
  }
  return t(strings.impossibleGroup, {
    count: fmt(violation.givers.length),
    peopleWord: tPlural(strings.peopleWord, locale, violation.givers.length),
    receivers: listNames(violation.receivers, names),
    givers: listNames(violation.givers, names),
  });
}

interface Draw {
  names: string[];
  receiver: number[];
  uniform: boolean;
}

export default function SecretSantaGeneratorWidget() {
  const strings = useToolUi(SECRET_SANTA_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [namesText, setNamesText] = useState("");
  const [rulesText, setRulesText] = useState("");
  const [noSwaps, setNoSwaps] = useState(false);
  const [note, setNote] = useState("");
  const [draw, setDraw] = useState<Draw | null>(null);
  const [shown, setShown] = useState<readonly number[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState("");
  const [announcement, setAnnouncement] = useState("");

  function fail(message: string) {
    setError(message);
    setDraw(null);
  }

  function describeFailure(
    result: Exclude<DrawResult, { kind: "ok" }>,
    names: string[]
  ) {
    if (result.kind === "impossible") {
      return impossibleMessage(result.violation, names, strings, locale, fmt);
    }
    return result.kind === "noSwapsImpossible"
      ? strings.noSwapsImpossible
      : strings.gaveUp;
  }

  function run() {
    const parsed = parseNames(namesText);
    if ("problem" in parsed) {
      const problem = parsed.problem;
      if (problem.kind === "duplicate") {
        fail(t(strings.problemDuplicate, { name: problem.name }));
      } else if (problem.kind === "tooMany") {
        fail(t(strings.problemTooMany, { max: fmt(MAX_PEOPLE) }));
      } else {
        fail(strings.problemTooFew);
      }
      return;
    }
    const rules = parseExclusions(rulesText, parsed.names);
    if ("problem" in rules) {
      const problem = rules.problem;
      fail(
        problem.kind === "unknown"
          ? t(strings.problemUnknown, {
              line: problem.line,
              name: problem.name,
            })
          : t(strings.problemUnreadable, { line: problem.line })
      );
      return;
    }
    const result = drawSantas(
      buildAllowed(parsed.names.length, rules.exclusions),
      noSwaps
    );
    if (result.kind !== "ok") {
      fail(describeFailure(result, parsed.names));
      return;
    }
    setError("");
    setShown([]);
    setShowAll(false);
    setDraw({
      names: parsed.names,
      receiver: result.receiver,
      uniform: result.uniform,
    });
    setAnnouncement(t(strings.announce, { count: fmt(parsed.names.length) }));
  }

  function toggle(index: number) {
    setShown(
      shown.includes(index)
        ? shown.filter((i) => i !== index)
        : [...shown, index]
    );
  }

  function messageFor(giver: number): string {
    if (!draw) {
      return "";
    }
    const values = {
      giver: draw.names[giver] ?? "",
      receiver: draw.names[draw.receiver[giver] ?? 0] ?? "",
      note: note.trim(),
    };
    return t(values.note ? strings.messageWithNote : strings.message, values);
  }

  const allPairs = draw
    ? draw.names
        .map((giver, i) =>
          t(strings.pairLine, {
            giver,
            receiver: draw.names[draw.receiver[i] ?? 0] ?? "",
          })
        )
        .join("\n")
    : "";

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <TextAreaField
          hint={t(strings.namesHint, { max: fmt(MAX_PEOPLE) })}
          id="ssg-names"
          label={strings.namesLabel}
          onChange={setNamesText}
          placeholder={strings.namesPlaceholder}
          prose
          rows={7}
          value={namesText}
        />
        <TextAreaField
          hint={strings.exclusionsHint}
          id="ssg-rules"
          label={strings.exclusionsLabel}
          onChange={setRulesText}
          placeholder={strings.exclusionsPlaceholder}
          prose
          rows={7}
          value={rulesText}
        />
      </div>
      <Checkbox
        checked={noSwaps}
        label={strings.noSwapsLabel}
        onChange={setNoSwaps}
      />
      <TextField
        id="ssg-note"
        label={strings.noteLabel}
        onChange={setNote}
        placeholder={strings.notePlaceholder}
        value={note}
      />
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_PRIMARY} onClick={run} type="button">
          {draw ? strings.redraw : strings.draw}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setNamesText(EXAMPLE_NAMES);
            setRulesText(EXAMPLE_RULES);
          }}
          type="button"
        >
          {strings.example}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setNamesText("");
            setRulesText("");
            setNote("");
            setDraw(null);
            setError("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <p className={HINT}>{strings.privacy}</p>
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        {draw ? (
          <>
            <p className={HINT}>{strings.resultsHint}</p>
            {draw.uniform ? null : <Notice>{strings.guided}</Notice>}
            <Checkbox
              checked={showAll}
              label={strings.showAll}
              onChange={setShowAll}
            />
            <ul className="space-y-2">
              {draw.names.map((giver, index) => {
                const visible = showAll || shown.includes(index);
                const receiver = draw.names[draw.receiver[index] ?? 0] ?? "";
                return (
                  <li
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3"
                    key={giver}
                  >
                    <p className="text-foreground text-sm">
                      <span className="font-medium">
                        {t(strings.gives, { giver })}
                      </span>{" "}
                      <span className={visible ? "" : "text-muted-foreground"}>
                        {visible ? receiver : strings.hidden}
                      </span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        aria-label={t(
                          visible ? strings.hideLabel : strings.showLabel,
                          { giver }
                        )}
                        className={BUTTON_SMALL}
                        onClick={() => toggle(index)}
                        type="button"
                      >
                        {visible ? strings.hide : strings.show}
                      </button>
                      <CopyButton
                        copiedLabel={strings.copied}
                        label={t(strings.copyMessage, { giver })}
                        small
                        text={messageFor(index)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              text={allPairs}
            />
          </>
        ) : (
          <Notice>{strings.placeholder}</Notice>
        )}
      </section>
    </div>
  );
}
